// src/utils/displayTimezone.js
//
// Fuseau d'AFFICHAGE des heures de trade (journal, calendrier, graphiques).
//
// Pourquoi ce réglage existe
// --------------------------
// Les horodatages des trades viennent du pont MT5 (`ia.fullmargin.net`). Ils
// sont rendus tels quels, sans aucune conversion côté front — vérifié. Or ils
// ne coïncident pas avec l'heure affichée par MT5 : un trade lu 19:17 dans le
// terminal ressort à 21:17 (constaté le 19/08/2026, navigateur en UTC+0). Le
// décalage est donc DANS LA DONNÉE, en amont de cette application.
//
// Tant que le pont n'expose pas le fuseau du serveur du courtier, la seule
// réponse honnête est de laisser le trader caler lui-même l'affichage sur son
// terminal, et de lui dire quel fuseau est appliqué.
//
// Sémantique
// ----------
// La valeur stockée est un décalage en MINUTES par rapport à UTC (ex: +120 pour
// UTC+2). `null` = heure locale du navigateur, comportement historique et
// défaut : rien ne change tant que l'utilisateur n'a rien choisi.
//
// Un fuseau explicite rend l'affichage DÉTERMINISTE : le même compte consulté
// depuis deux machines dans deux pays montre les mêmes heures. En mode local,
// non — c'est le comportement d'aujourd'hui.

const STORAGE_KEY = "fmx:displayTzOffsetMin";
/** Événement émis à chaque changement, pour re-rendre les vues ouvertes. */
export const DISPLAY_TZ_EVENT = "fmx:display-tz-changed";

/** Décalage choisi, en minutes par rapport à UTC. `null` = heure locale. */
export function getDisplayOffsetMin() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null || raw === "" || raw === "local") return null;
    const n = Number(raw);
    // Bornes réelles des fuseaux : UTC-12 → UTC+14.
    if (!Number.isFinite(n) || n < -720 || n > 840) return null;
    return n;
  } catch {
    return null;
  }
}

export function setDisplayOffsetMin(min) {
  try {
    if (min == null) localStorage.setItem(STORAGE_KEY, "local");
    else localStorage.setItem(STORAGE_KEY, String(min));
    window.dispatchEvent(new CustomEvent(DISPLAY_TZ_EVENT));
  } catch {
    /* stockage indisponible (navigation privée) → réglage non persisté */
  }
}

/**
 * Décale un instant pour que les getters LOCAUX (`getHours`, `toLocaleString`…)
 * restituent l'heure murale du fuseau choisi.
 *
 * On retire le décalage du navigateur avant d'appliquer celui de l'utilisateur,
 * sinon les deux s'additionneraient et le résultat dépendrait de la machine.
 * Renvoie l'instant inchangé en mode local : aucun coût, aucun effet de bord.
 */
export function applyDisplayOffset(date) {
  if (!date) return date;
  const target = getDisplayOffsetMin();
  if (target == null) return date;
  const browser = -date.getTimezoneOffset(); // minutes à l'est d'UTC
  return new Date(date.getTime() + (target - browser) * 60000);
}

/** Libellé court d'un décalage : 120 → "UTC+2", -330 → "UTC-5:30". */
export function offsetLabel(min) {
  if (min == null) return "Heure locale";
  const sign = min < 0 ? "-" : "+";
  const abs = Math.abs(min);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${h}${m ? `:${String(m).padStart(2, "0")}` : ""}`;
}

/** Décalage du navigateur, pour afficher ce que vaut le mode « local ». */
export function browserOffsetMin() {
  return -new Date().getTimezoneOffset();
}

/** Choix proposés dans le sélecteur : UTC-12 → UTC+14, pas d'une heure. */
export const OFFSET_CHOICES = Array.from(
  { length: 27 },
  (_, i) => (i - 12) * 60,
);
